/**
 * Menus Tab Component
 *
 * Tab content for managing POS menus in Settings page.
 * @see Sprint 25a - Gestion des Promotions
 */

import { useState } from 'react';
import { Search, Menu, RefreshCw } from 'lucide-react';
import { Input, Skeleton, EmptyState, Button } from '@/components/ui';
import { usePosMenus, useUpdatePosMenu, useSyncMenus } from '../../../pos-menus';
import { useDebounce } from '@bill/ui';
import type { PosMenu } from '@bstock/shared';

export function MenusTab() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading, error } = usePosMenus({
    search: debouncedSearch || undefined,
  });

  const updateMutation = useUpdatePosMenu();
  const syncMutation = useSyncMenus();

  const handleTogglePromo = async (menu: PosMenu) => {
    await updateMutation.mutateAsync({
      id: menu.id,
      input: { isPromo: !menu.isPromo },
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center py-8">
        Erreur lors du chargement des menus
      </div>
    );
  }

  const menus = data?.items ?? [];
  const promoCount = menus.filter((m) => m.isPromo).length;

  return (
    <div className="space-y-4">
      {/* Header with search and sync button */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder="Rechercher un menu..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          variant="secondary"
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
          {syncMutation.isPending ? 'Synchronisation...' : 'Synchroniser depuis Bill'}
        </Button>
      </div>

      {/* Table */}
      {menus.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-xl p-12 text-center">
          <EmptyState
            icon={Menu}
            title="Aucun menu importe"
            description="Les menus seront automatiquement decouverts lors de la synchronisation des ventes."
            size="lg"
          />
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">
                  Nom
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600 w-40">
                  Barcode
                </th>
                <th className="text-center px-4 py-3 text-sm font-medium text-neutral-600 w-24">
                  Promo
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {menus.map((menu) => (
                <tr key={menu.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3 text-sm text-neutral-900">
                    {menu.name}
                  </td>
                  <td className="px-4 py-3 text-sm text-neutral-500 font-mono">
                    {menu.barcode}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={menu.isPromo}
                        onChange={() => handleTogglePromo(menu)}
                        disabled={updateMutation.isPending}
                        className="sr-only peer"
                      />
                      <div className={`
                        w-11 h-6 bg-neutral-200 rounded-full
                        peer-checked:bg-primary-600
                        peer-focus:ring-2 peer-focus:ring-primary-500 peer-focus:ring-offset-2
                        peer-disabled:opacity-50 peer-disabled:cursor-not-allowed
                        after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                        after:bg-white after:rounded-full after:h-5 after:w-5
                        after:transition-all after:shadow
                        peer-checked:after:translate-x-5
                      `}></div>
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Stats footer */}
      {menus.length > 0 && (
        <div className="text-sm text-neutral-500">
          {data?.total ?? menus.length} menus importes | {promoCount} marques comme promo
        </div>
      )}
    </div>
  );
}
